import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';
import { Shift } from '../../components/shiftcard/ShiftCard';

// ========================================
// TYPES
// ========================================
export type AvailableShiftsFilter = 'all' | 'thisweek' | 'today';

// Shape of a shift as returned by the API (includes the new slot counts and id aliases)
type AvailableShiftResponse = Shift & {
    openSlots?: number;
    pendingSlots?: number;
    requestId?: number | string;
    shiftId?: string;
    requestShiftId?: string;
};

interface AvailableShiftsState {
    shifts: Shift[];
    loading: boolean;
    error: string | null;
    filter: AvailableShiftsFilter;
}

const initialState: AvailableShiftsState = {
    shifts: [],
    loading: false,
    error: null,
    filter: 'all',
};

// ========================================
// HELPERS
// ========================================
const deriveStatus = (shift: AvailableShiftResponse): Shift['status'] => {
    if ((shift.openSlots ?? 0) > 0) {
        return 'PST';
    }
    if ((shift.pendingSlots ?? 0) > 0) {
        return 'PND';
    }
    return shift.status;
};

const deriveId = (shift: AvailableShiftResponse): string => {
    const raw = shift.id ?? shift.requestId ?? shift.shiftId ?? shift.requestShiftId;
    return raw !== undefined && raw !== null ? String(raw) : '';
};

const normalizeShifts = (payload: AvailableShiftResponse[]): Shift[] =>
    (payload ?? []).map((s) => ({
        ...s,
        id: deriveId(s),
        status: deriveStatus(s),
        availableCount: s.openSlots ?? s.availableCount ?? 0,
    }));

// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Fetch available shifts for a site with optional filter and required skill codes
 * Uses: GET /api/shift/availableShifts/{siteCd}?filter=all|thisweek|today&requiredSkillCds=AAA&requiredSkillCds=BBB
 */
export const fetchAvailableShifts = createAsyncThunk(
    'availableShifts/fetchAvailableShifts',
    async (
        {
            siteCd,
            filter = 'all',
            userId,
        }: { siteCd: string; filter?: AvailableShiftsFilter; userId?: string },
        { rejectWithValue }
    ) => {
        try {
            const params = new URLSearchParams();
            params.append('filter', filter);
            if (userId) {
                params.append('userId', userId);
            }

            const response = await API_FETCH(
                `${ENDPOINTS.GET_AVAILABLE_SHIFTS}/${siteCd}?${params.toString()}`
            );
            console.log('✅ Available Shifts API Response:', response);
            return response as AvailableShiftResponse[];
        } catch (error: any) {
            console.error('❌ Available Shifts API Error:', error);
            return rejectWithValue(error?.message || 'Failed to fetch available shifts');
        }
    }
);

// ========================================
// SLICE
// ========================================
const availableShiftsSlice = createSlice({
    name: 'availableShifts',
    initialState,
    reducers: {
        setFilter: (state, action) => {
            state.filter = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAvailableShifts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAvailableShifts.fulfilled, (state, action) => {
                state.loading = false;
                state.shifts = normalizeShifts(action.payload);
            })
            .addCase(fetchAvailableShifts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setFilter, clearError } = availableShiftsSlice.actions;
export default availableShiftsSlice.reducer;